import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { useCart } from '../context/CartContext';
import { SHIPPING_NOTICE, SHIPPING_COUNTRIES, CONTACT_INFO, categories, SHIPPING_METHOD, SHIPPING_TIME, CARD_DESCRIPTOR, FREE_SHIPPING_THRESHOLD } from '../data/mock';
import { Lock, Truck, ArrowLeft, AlertCircle, ShoppingCart, Shield, Phone, Tag, CheckCircle2, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Payment Card SVG Components
const VisaLogo = () => (
  <svg className="h-5 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="750" height="471" rx="40" fill="#1A1F71"/>
    <path d="M278.198 334.228L311.058 138.432H364.028L331.158 334.228H278.198ZM524.308 142.688C513.958 138.792 497.468 134.528 477.168 134.528C424.888 134.528 388.028 162.108 387.698 201.628C387.368 231.208 414.618 247.698 435.248 257.388C456.538 267.408 463.778 273.688 463.668 282.618C463.558 296.218 447.288 302.278 432.088 302.278C411.018 302.278 399.798 299.038 382.428 291.358L375.518 288.008L367.928 333.038C380.418 338.618 403.378 343.468 427.228 343.688C482.808 343.688 518.888 316.548 519.328 274.568C519.548 250.828 504.568 232.658 472.818 217.718C453.728 208.238 441.898 201.848 442.008 192.258C442.008 183.658 451.918 174.398 473.198 174.398C490.898 174.068 503.828 177.858 514.068 181.758L518.998 184.008L526.478 140.408L524.308 142.688ZM661.628 138.432H620.028C607.208 138.432 597.628 141.888 591.708 154.598L513.628 334.228H569.098L580.318 304.208H647.878L654.238 334.228H703.198L661.628 138.432ZM596.208 264.878C600.538 253.698 617.578 208.458 617.578 208.458C617.248 209.008 621.798 197.278 624.448 189.818L628.008 206.858C628.008 206.858 638.568 255.598 640.888 264.878H596.208ZM232.898 138.432L181.068 267.958L175.578 241.698C166.098 210.098 136.958 175.768 104.208 158.618L151.578 333.898H207.488L288.478 138.432H232.898Z" fill="white"/>
    <path d="M131.918 138.432H45.7784L45.0084 142.578C112.188 159.728 156.548 199.908 175.578 241.698L156.108 154.928C152.658 142.328 143.188 138.872 131.918 138.432Z" fill="#F9A51A"/>
  </svg>
);

const MastercardLogo = () => (
  <svg className="h-6 w-auto" viewBox="0 0 152 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="152" height="100" rx="8" fill="#ffffff" stroke="#e5e7eb"/>
    <circle cx="60" cy="50" r="30" fill="#EB001B"/>
    <circle cx="92" cy="50" r="30" fill="#F79E1B"/>
    <path d="M76 25.6C83.3 31.3 88 40.1 88 50C88 59.9 83.3 68.7 76 74.4C68.7 68.7 64 59.9 64 50C64 40.1 68.7 31.3 76 25.6Z" fill="#FF5F00"/>
  </svg>
);

const AmexLogo = () => (
  <svg className="h-5 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="750" height="471" rx="40" fill="#006FCF"/>
    <path d="M0 373.625V296.25H41.875L50 312.375L58.375 296.25H365.625V369.125C365.625 369.125 359.125 373.625 350.625 373.625H213.5L204.125 362.5V373.625H166.375V352.5C166.375 352.5 162.625 355.625 153.625 355.625H141.75V373.625H58.125L50 357.125L41.625 373.625H0Z" fill="white"/>
    <path d="M0 97.375L18.75 51.625H61.25L71.125 73.625V51.625H124.375L132.75 74.625L140.875 51.625H365.625V60.375C365.625 60.375 355.875 51.625 339.625 51.625H236.125L226.25 73.625V51.625H179.625L171.25 74.625L163.125 51.625H119.5L110.875 73.625V51.625H72.625L50 105.625L27.375 51.625H0V97.375Z" fill="white"/>
    <text x="400" y="280" fill="white" fontFamily="Arial" fontWeight="bold" fontSize="90">AMEX</text>
  </svg>
);

const inputCls = "mt-2 rounded-lg border-dark-200 bg-white text-dark-900 placeholder-dark-400 focus:ring-athletic-500 focus:border-athletic-500";

const StepHeader = ({ n, title }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-athletic-500 to-power-600 text-white font-bold text-sm flex items-center justify-center flex-shrink-0">{n}</span>
    <h2 className="font-display text-xl text-dark-900 font-bold">{title}</h2>
  </div>
);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [billingCountry, setBillingCountry] = useState('US');
  const [countryError, setCountryError] = useState(false);
  const [billingCountryError, setBillingCountryError] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');

  const PROMO_CODE = 'BRIGHT20';
  const PROMO_DISCOUNT_PERCENT = 0.20;

  const discount = appliedPromo ? cartTotal * PROMO_DISCOUNT_PERCENT : 0;
  const discountedSubtotal = cartTotal - discount;
  const shipping = discountedSubtotal >= FREE_SHIPPING_THRESHOLD ? 0 : 9.95;
  const tax = discountedSubtotal * 0.0825;
  const total = discountedSubtotal + shipping + tax;

  useEffect(() => {
    document.title = 'Checkout | Bright Tech Supply';
  }, []);

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setCountryError(!SHIPPING_COUNTRIES.includes(country));
  };

  const handleBillingCountryChange = (e) => {
    const country = e.target.value;
    setBillingCountry(country);
    setBillingCountryError(!SHIPPING_COUNTRIES.includes(country));
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    if (e.target.checked) setTermsError(false);
  };

  const handleApplyPromo = (e) => {
    e.preventDefault();
    setPromoError('');
    const code = promoInput.trim().toUpperCase();
    if (!code) { setPromoError('Please enter a promo code'); return; }
    if (code === PROMO_CODE) {
      setAppliedPromo({ code: PROMO_CODE, percent: PROMO_DISCOUNT_PERCENT });
      setPromoError('');
    } else {
      setAppliedPromo(null);
      setPromoError('Invalid promo code. Try BRIGHT20 for 20% off.');
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!SHIPPING_COUNTRIES.includes(selectedCountry)) { setCountryError(true); return; }
    if (!billingSameAsShipping && !SHIPPING_COUNTRIES.includes(billingCountry)) { setBillingCountryError(true); return; }
    if (!termsAccepted) { setTermsError(true); return; }
    setIsProcessing(true);
    setTimeout(() => { clearCart(); navigate('/order-confirmation'); }, 2000);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-24 h-24 bg-dark-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ShoppingCart className="w-10 h-10 text-dark-300" />
            </div>
            <h1 className="font-display text-3xl text-dark-900 font-bold tracking-tight mb-4">Your Cart is Empty</h1>
            <p className="text-dark-500 mb-8">Add a gadget or two to get started.</p>
            <Link to="/shop">
              <button className="px-8 py-4 bg-gradient-to-r from-athletic-500 to-power-600 hover:opacity-90 text-white font-bold uppercase tracking-wider rounded-lg transition-all shadow-button">
                Browse Gadgets
              </button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-50">
      <Header />
      <CartDrawer />

      <main className="py-8 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/shop" className="inline-flex items-center gap-2 text-dark-500 hover:text-athletic-600 text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Continue Shopping
          </Link>

          <h1 className="font-display text-3xl text-dark-900 font-bold tracking-tight mb-6">Secure Checkout</h1>

          {/* Notices */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="p-4 bg-white border border-dark-100 rounded-xl flex items-center gap-3">
              <Truck className="w-5 h-5 text-athletic-600 flex-shrink-0" />
              <p className="text-dark-700 font-medium text-sm">{SHIPPING_NOTICE}</p>
            </div>
            <div className="p-4 bg-gradient-to-r from-athletic-50 to-power-50 border border-athletic-200 rounded-xl flex items-center gap-3">
              <Tag className="w-5 h-5 text-athletic-600 flex-shrink-0" />
              <p className="text-dark-700 font-semibold text-sm">
                Use code <span className="px-2 py-0.5 bg-dark-900 text-white font-extrabold tracking-wider rounded text-xs">BRIGHT20</span> for 20% OFF
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Info */}
                <div className="bg-white p-6 md:p-8 rounded-2xl border border-dark-100 shadow-sm">
                  <StepHeader n={1} title="Contact Information" />
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-dark-900">Email</Label>
                      <Input id="email" type="email" className={inputCls} placeholder="your@email.com" required />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-dark-900">Phone</Label>
                      <Input id="phone" type="tel" className={inputCls} placeholder="(555) 555-5555" />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white p-6 md:p-8 rounded-2xl border border-dark-100 shadow-sm">
                  <StepHeader n={2} title="Shipping Address" />
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">First Name</Label>
                        <Input className={inputCls} required />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">Last Name</Label>
                        <Input className={inputCls} required />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-dark-900">Country</Label>
                      <select value={selectedCountry} onChange={handleCountryChange}
                        className={`mt-2 w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all bg-white text-dark-900 ${countryError ? 'border-red-500 focus:ring-red-500' : 'border-dark-200 focus:ring-athletic-500 focus:border-athletic-500'}`} required>
                        <option value="US">United States</option>
                      </select>
                      {countryError && (
                        <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                          <AlertCircle className="w-4 h-4" /><span>Sorry, we only ship within the USA</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-dark-900">Address Line 1</Label>
                      <Input className={inputCls} placeholder="123 Main Street" required />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-dark-900">Address Line 2 <span className="text-dark-400 font-normal">(optional)</span></Label>
                      <Input className={inputCls} placeholder="Apartment, suite, unit, etc." />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">City</Label>
                        <Input className={inputCls} required />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">State</Label>
                        <Input className={inputCls} required />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">ZIP</Label>
                        <Input className={inputCls} required />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-white p-6 md:p-8 rounded-2xl border border-dark-100 shadow-sm">
                  <StepHeader n={3} title="Billing Address" />
                  <div className="flex items-start gap-3 mb-6 p-4 bg-dark-50 border border-dark-100 rounded-xl">
                    <input type="checkbox" id="billing-same" checked={billingSameAsShipping} onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-2 border-dark-300 text-athletic-500 focus:ring-athletic-500 cursor-pointer flex-shrink-0 bg-white" />
                    <label htmlFor="billing-same" className="cursor-pointer text-sm text-dark-900 font-medium">
                      Billing address is the same as shipping address
                    </label>
                  </div>

                  {!billingSameAsShipping && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold text-dark-900">First Name</Label>
                          <Input className={inputCls} required />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-dark-900">Last Name</Label>
                          <Input className={inputCls} required />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">Country</Label>
                        <select value={billingCountry} onChange={handleBillingCountryChange}
                          className={`mt-2 w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all bg-white text-dark-900 ${billingCountryError ? 'border-red-500 focus:ring-red-500' : 'border-dark-200 focus:ring-athletic-500 focus:border-athletic-500'}`} required>
                          <option value="US">United States</option>
                        </select>
                        {billingCountryError && (
                          <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                            <AlertCircle className="w-4 h-4" /><span>Billing address must be in the USA</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">Address Line 1</Label>
                        <Input className={inputCls} placeholder="123 Main Street" required />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">Address Line 2 <span className="text-dark-400 font-normal">(optional)</span></Label>
                        <Input className={inputCls} placeholder="Apartment, suite, unit, etc." />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm font-semibold text-dark-900">City</Label>
                          <Input className={inputCls} required />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-dark-900">State</Label>
                          <Input className={inputCls} required />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-dark-900">ZIP</Label>
                          <Input className={inputCls} required />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment */}
                <div className="bg-white p-6 md:p-8 rounded-2xl border border-dark-100 shadow-sm">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <StepHeader n={4} title="Payment Details" />
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-dark-400 font-medium">We Accept</span>
                      <div className="flex items-center gap-2"><VisaLogo /><MastercardLogo /><AmexLogo /></div>
                    </div>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <Label className="text-sm font-semibold text-dark-900">Card Number</Label>
                      <Input className={inputCls} placeholder="4242 4242 4242 4242" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">Expiry Date</Label>
                        <Input className={inputCls} placeholder="MM/YY" required />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-dark-900">CVV</Label>
                        <Input className={inputCls} placeholder="123" required />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-5 p-3 bg-athletic-50 border border-athletic-200 rounded-xl text-athletic-700 text-sm">
                    <Lock className="w-4 h-4" /><span className="font-medium">SSL Secured 128-bit encryption</span>
                  </div>
                </div>

                {/* Terms */}
                <div className="bg-white border border-dark-100 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <input type="checkbox" id="terms-checkbox" checked={termsAccepted} onChange={handleTermsChange}
                      className={`mt-0.5 w-5 h-5 rounded border-2 text-athletic-500 focus:ring-athletic-500 cursor-pointer flex-shrink-0 bg-white ${termsError ? 'border-red-500' : 'border-dark-300'}`} />
                    <label htmlFor="terms-checkbox" className="cursor-pointer">
                      <span className="font-semibold text-dark-900">
                        I agree to the{' '}
                        <Link to="/terms" className="text-athletic-600 hover:underline">Terms &amp; Conditions</Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-athletic-600 hover:underline">Privacy Policy</Link>
                      </span>
                      <p className="mt-3 text-sm text-dark-500 leading-relaxed">
                        By clicking Complete Checkout, you authorize a one-time charge for the product amount shown above.
                        Shipping is free on every order. All orders are delivered by {SHIPPING_METHOD}; please allow {SHIPPING_TIME} for delivery.
                      </p>
                      <p className="mt-2 text-sm text-dark-500 leading-relaxed">
                        If this product is not for you, please contact customer service at{' '}
                        <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-athletic-600 hover:underline font-medium">{CONTACT_INFO.phone}</a>{' '}
                        or email us at{' '}
                        <a href={`mailto:${CONTACT_INFO.email}`} className="text-athletic-600 hover:underline font-medium">{CONTACT_INFO.email}</a>.
                      </p>
                      <p className="mt-3 text-sm text-dark-900 font-semibold bg-dark-50 p-3 rounded-lg border border-dark-100">
                        All charges will appear as &ldquo;{CARD_DESCRIPTOR}&rdquo; on your credit card statement.
                      </p>
                    </label>
                  </div>
                  {termsError && (
                    <div className="mt-4 flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>Please accept the Terms &amp; Conditions to continue</span>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={isProcessing || countryError}
                  className="w-full py-4 bg-gradient-to-r from-athletic-500 to-power-600 hover:opacity-90 text-white font-bold uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-button">
                  {isProcessing ? 'Processing...' : `Complete Checkout — $${total.toFixed(2)}`}
                </button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-2xl border border-dark-100 shadow-sm sticky top-28">
                <h2 className="font-display text-xl text-dark-900 font-bold mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  {cartItems.map(item => {
                    const itemCategory = categories.find(c => c.id === item.categoryId);
                    return (
                      <div key={item.id} className="flex gap-4">
                        <div className="w-16 h-16 bg-dark-50 rounded-xl flex-shrink-0 overflow-hidden">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-athletic-600 uppercase font-semibold">{itemCategory?.name}</p>
                          <h3 className="text-sm font-medium text-dark-900 line-clamp-1">{item.name}</h3>
                          <p className="text-dark-400 text-sm">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-dark-900 font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-dark-100 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-dark-500">Subtotal</span>
                    <span className="text-dark-900">${cartTotal.toFixed(2)}</span>
                  </div>
                  {appliedPromo && (
                    <div className="flex justify-between text-sm">
                      <span className="text-athletic-700 font-semibold flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5" /> Discount ({appliedPromo.code} &mdash; {Math.round(appliedPromo.percent * 100)}% off)
                      </span>
                      <span className="text-athletic-700 font-semibold">-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-dark-500">Shipping</span>
                    <span className="text-dark-900">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-dark-500">Tax</span>
                    <span className="text-dark-900">${tax.toFixed(2)}</span>
                  </div>
                </div>

                {/* Promo Code Input */}
                <div className="mt-4 pt-4 border-t border-dark-100">
                  {!appliedPromo ? (
                    <div>
                      <label className="text-xs font-semibold text-dark-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                        <Tag className="w-3.5 h-3.5 text-athletic-600" /> Promo Code
                      </label>
                      <div className="flex gap-2">
                        <input type="text" value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(e); } }}
                          placeholder="Enter code (e.g. BRIGHT20)"
                          className={`flex-1 px-3 py-2.5 rounded-lg border bg-white text-sm text-dark-900 placeholder-dark-400 focus:outline-none focus:ring-2 transition-all ${promoError ? 'border-red-400 focus:ring-red-400' : 'border-dark-200 focus:ring-athletic-500 focus:border-athletic-500'}`} />
                        <button type="button" onClick={handleApplyPromo}
                          className="px-4 py-2.5 bg-dark-900 hover:bg-dark-800 text-white font-bold text-sm uppercase tracking-wide rounded-lg transition-colors">
                          Apply
                        </button>
                      </div>
                      {promoError && (
                        <div className="mt-2 flex items-center gap-1.5 text-red-500 text-xs">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /><span>{promoError}</span>
                        </div>
                      )}
                      <p className="mt-2 text-xs text-dark-500">
                        Try code <span className="font-bold text-athletic-600">BRIGHT20</span> for 20% off!
                      </p>
                    </div>
                  ) : (
                    <div className="bg-athletic-50 border border-athletic-200 rounded-xl p-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-5 h-5 text-athletic-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-athletic-700 truncate">Code {appliedPromo.code} applied</p>
                          <p className="text-xs text-athletic-600">You saved ${discount.toFixed(2)} ({Math.round(appliedPromo.percent * 100)}% off)</p>
                        </div>
                      </div>
                      <button type="button" onClick={handleRemovePromo}
                        className="p-1.5 rounded-lg text-athletic-700 hover:bg-athletic-100 transition-colors flex-shrink-0" aria-label="Remove promo code">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-t border-dark-100 mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-display text-xl text-dark-900 font-bold">Total</span>
                    <span className="font-display text-2xl text-dark-900 font-bold">${total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-dark-100 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-dark-600">
                    <Truck className="w-4 h-4 text-athletic-600" />
                    {discountedSubtotal >= FREE_SHIPPING_THRESHOLD ? 'Free shipping on this order!' : `Add $${(FREE_SHIPPING_THRESHOLD - discountedSubtotal).toFixed(2)} for free shipping`}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-dark-600">
                    <Shield className="w-4 h-4 text-athletic-600" /> Secure checkout guaranteed
                  </div>
                  <div className="text-xs text-dark-400 mt-2">All prices displayed in USD</div>
                </div>

                <div className="mt-6 pt-6 border-t border-dark-100">
                  <p className="text-xs text-dark-400 mb-2">Need help?</p>
                  <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 text-sm text-athletic-600 hover:text-athletic-700 transition-colors">
                    <Phone className="w-4 h-4" /> {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
